/**
 * Pledges Summary Card Component
 *
 * Displays a summary of the donor's active pledges:
 * - Total pending pledge count and amount
 * - Confirmed pledge count and amount
 */

import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { HandRaisedIcon } from '@heroicons/react/24/outline';
import type { UsePledgesResult } from '@/hooks/usePledges';

interface PledgesSummaryCardProps {
  pledgesData: UsePledgesResult;
}

function fmt(amount: number) {
  return amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export const PledgesSummaryCard: FC<PledgesSummaryCardProps> = ({ pledgesData }) => {
  const navigate = useNavigate();
  const { isLoading, error, pendingCount, pendingAmount, confirmedCount, confirmedAmount } =
    pledgesData;

  return (
    <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow h-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-emerald-50">
            <HandRaisedIcon className="h-6 w-6 text-emerald-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">My Pledges</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/pledge')}
          className="text-xs font-medium text-emerald-600 hover:text-emerald-700 border border-emerald-200 rounded-lg px-3 py-1.5 hover:bg-emerald-50 transition-colors"
        >
          + New Pledge
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-emerald-600" />
        </div>
      ) : error ? (
        <p className="text-sm text-red-500 py-2">Unable to load pledges.</p>
      ) : (
        <div className="grid grid-cols-2 divide-x divide-gray-100">
          {/* Pending */}
          <div className="pr-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Pending</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">${fmt(Number(pendingAmount))}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {pendingCount} pledge{pendingCount !== 1 ? 's' : ''}
            </p>
          </div>
          {/* Confirmed */}
          <div className="pl-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Confirmed</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">${fmt(Number(confirmedAmount))}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {confirmedCount} pledge{confirmedCount !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
